import { useT, type TranslationKey } from '../../i18n';
import { formatMoneyShort, formatNumber } from '../../lib/reportFormat';
import type { Confidence, PipelineReport } from '../../types/api';
import styles from './BasketPanel.module.css';

const BASKETS: {
  key: Confidence;
  labelKey: TranslationKey;
  nameKey: TranslationKey;
}[] = [
  { key: 'K90', labelKey: 'confidence.k90', nameKey: 'confidence.k90Label' },
  { key: 'K50', labelKey: 'confidence.k50', nameKey: 'confidence.k50Label' },
  { key: 'K10', labelKey: 'confidence.k10', nameKey: 'confidence.k10Label' },
];

export interface BasketPanelProps {
  portfolio: PipelineReport['portfolio'];
  /** Opens the drill-in for a basket. */
  onSelect: (basket: Confidence) => void;
}

/**
 * Confidence baskets К90/К50/К10: counts, sums and share of the portfolio.
 * Click a basket to drill into its deals.
 */
export function BasketPanel({ portfolio, onSelect }: BasketPanelProps) {
  const { t, locale } = useT();

  const totalCount = BASKETS.reduce((n, b) => n + portfolio[b.key].count, 0);
  const totalSum = BASKETS.reduce((n, b) => n + portfolio[b.key].sum, 0);

  return (
    <div className={styles.root}>
      <div className={styles.baskets}>
        {BASKETS.map((b) => {
          const bucket = portfolio[b.key];
          const share = totalSum > 0 ? Math.round((bucket.sum / totalSum) * 100) : 0;
          return (
            <button
              key={b.key}
              type="button"
              className={styles.basket}
              onClick={() => onSelect(b.key)}
            >
              <div className={styles.basketTop}>
                <span className={`${styles.chip} ${styles[`chip_${b.key}`]}`}>
                  {t(b.labelKey)}
                </span>
                <span className={styles.basketName}>{t(b.nameKey)}</span>
              </div>
              <div className={styles.basketNums}>
                <span className={styles.basketDeals}>{formatNumber(bucket.count, locale)}</span>
                <span className={styles.basketSum}>
                  {formatMoneyShort(bucket.sum, locale, t('pipeline.millionShort'))}
                </span>
              </div>
              <div
                className={styles.basketShare}
                role="img"
                aria-label={t('pipeline.basketShareHint', { share })}
              >
                <i className={styles[`fill_${b.key}`]} style={{ width: `${share}%` }} />
              </div>
              <div className={styles.basketHint}>
                {t('pipeline.basketShareHint', { share })} · {t('pipeline.basketOpen')}
              </div>
            </button>
          );
        })}
      </div>
      <div className={styles.balance}>
        {t('pipeline.portfolioBalance')}{' '}
        <b>{t('pipeline.dealsCount', { count: formatNumber(totalCount, locale) })}</b> ·{' '}
        <b>{formatMoneyShort(totalSum, locale, t('pipeline.millionShort'))}</b>
      </div>
    </div>
  );
}
